import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { ChargingLocation, ChargePoint } from '../../types';
import { Sheet, SheetContent, SheetTitle, SheetDescription } from './ui/sheet';
import { MapPin, PlugZap, Navigation, X, Info, Hotel, Coffee, ShoppingBag, Store, Dumbbell, TreePine, Landmark, Bike, Bus, Car, Train, Plane, ParkingCircle, Fuel, Wifi, Power, CarFront, Heart } from 'lucide-react';
import Slider from 'react-slick';
import 'slick-carousel/slick/slick.css';
import 'slick-carousel/slick/slick-theme.css';
import { locationService } from '../../services/locationService';
import { transformLocationToChargingLocation, toDetailedChargePoint } from '../../utils/transformers';
import { useLocationRealtimeStatus } from '../../store/locationStatusStore';
import { useFavoritesStore } from '../../store/favoritesStore';
import { captureError } from '../../lib/errorHandler';
import { ChargePointConnectors, type ChargePointStatusInfo } from './ChargePointConnectors';

interface LocationDetailSheetProps {
  location: ChargingLocation | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onStartCharging?: (chargePointId: string, connectorId: string) => void;
  isStartingCharge?: boolean;
}

/**
 * The backend returns the raw haversine result, so distances arrive with full
 * float precision. One decimal is enough to be useful and keeps sub-kilometre
 * distances meaningful, where a whole-number round would flatten them to "0 km".
 */
const formatDistance = (km: number): string => {
  const rounded = Math.round(km * 10) / 10;
  return Number.isInteger(rounded) ? String(rounded) : rounded.toFixed(1);
};

// Facility icon mapping
const getFacilityIcon = (facility: string) => {
  const facilityMap: { [key: string]: { icon: React.ComponentType<{ className?: string }>, label: string } } = {
    'HOTEL': { icon: Hotel, label: 'Hotel' },
    'RESTAURANT': { icon: Coffee, label: 'Restaurant' },
    'CAFE': { icon: Coffee, label: 'Café' },
    'MALL': { icon: ShoppingBag, label: 'Mall' },
    'SUPERMARKET': { icon: Store, label: 'Market' },
    'SPORT': { icon: Dumbbell, label: 'Sport' },
    'RECREATION AREA': { icon: Dumbbell, label: 'Recreation' },
    'NATURE': { icon: TreePine, label: 'Nature' },
    'MUSEUM': { icon: Landmark, label: 'Museum' },
    'BIKE SHARING': { icon: Bike, label: 'Bike' },
    'BUS STOP': { icon: Bus, label: 'Bus' },
    'TAXI STAND': { icon: Car, label: 'Taxi' },
    'TRAM STOP': { icon: Bus, label: 'Tram' },
    'METRO STATION': { icon: Train, label: 'Metro' },
    'TRAIN STATION': { icon: Train, label: 'Train' },
    'AIRPORT': { icon: Plane, label: 'Airport' },
    'PARKING LOT': { icon: ParkingCircle, label: 'Parking' },
    'CARPOOL PARKING': { icon: Car, label: 'Carpool' },
    'FUEL STATION': { icon: Fuel, label: 'Fuel' },
    'WIFI': { icon: Wifi, label: 'WiFi' },
    'POWER': { icon: Power, label: 'Power' },
  };
  
  return facilityMap[facility] || { icon: Info, label: facility };
};

export function LocationDetailSheet({ location: locationProp, open, onOpenChange, onStartCharging, isStartingCharge }: LocationDetailSheetProps) {
  // The prop is resolved out of the map's nearby list, which can transiently drop
  // the selected location — a refetch that lands outside the search radius empties
  // the store until MapPage's by-id fallback refills it. Blanking the sheet on that
  // blink used to be invisible; now it would tear down every gun's status poll and
  // tariff fetch and rebuild them seconds later. Hold the last good copy instead.
  const lastLocationRef = useRef<ChargingLocation | null>(null);
  if (locationProp) lastLocationRef.current = locationProp;
  const location = locationProp ?? lastLocationRef.current;

  const [freshLocation, setFreshLocation] = useState<ChargingLocation | null>(null);
  const { isFavorite, toggleFavorite } = useFavoritesStore();
  const isFav = location ? isFavorite(location.id) : false;

  // Fetch fresh location data when drawer opens
  useEffect(() => {
    if (open && location?.id) {
      locationService.getLocationById(location.id)
        .then(rawLocation => {
          const transformed = transformLocationToChargingLocation(rawLocation);
          setFreshLocation(transformed);
        })
        .catch(err => {
          captureError(err, 'LocationDetailSheet.refresh');
        });
    } else {
      setFreshLocation(null);
    }
  }, [open, location?.id]);

  // Use fresh chargepoints when available, fall back to prop data
  const chargepoints = freshLocation?.chargepoints ?? location?.chargepoints ?? [];

  // Provisional order from the location payload, used only until the chargers
  // report their live status. The payload's status and isOnline are a snapshot of
  // whenever the location was last written, so it cannot be trusted to rank them.
  const sortedChargepoints = useMemo(() => {
    const priority = (cp: ChargePoint): number => {
      if (cp.isOnline === false) return 3;
      if (cp.status === 'available') return 0;
      if (cp.status === 'occupied') return 1;
      return 2;
    };
    return [...chargepoints].sort((a, b) => priority(a) - priority(b));
  }, [chargepoints]);

  // One gun open at a time, across the whole location rather than per charger —
  // otherwise the list this disclosure exists to shorten can grow straight back.
  const [expanded, setExpanded] = useState<{ id: string | null; byUser: boolean }>({
    id: null,
    byUser: false,
  });
  const expandedConnectorId = expanded.id;
  const userToggledRef = useRef(false);
  // cpId -> all its gun ids, as each charger reports in.
  const allGunsRef = useRef(new Map<string, string[]>());
  // Live ranking of the chargers: online with a free gun, then online, then
  // offline. Latched once every charger has reported so a poll cannot reshuffle
  // the list while it is being read; reopening the sheet recomputes it.
  const [liveOrder, setLiveOrder] = useState<string[] | null>(null);
  const orderLatchedRef = useRef(false);
  const liveInfoRef = useRef(new Map<string, ChargePointStatusInfo>());

  const displayChargepoints = useMemo(() => {
    if (!liveOrder) return sortedChargepoints;
    return [...sortedChargepoints].sort(
      (a, b) => liveOrder.indexOf(a.id) - liveOrder.indexOf(b.id)
    );
  }, [sortedChargepoints, liveOrder]);

  // Display order, so the lone-gun sweep and the ranking below read the chargers
  // in the order shown, no matter which charger's fetch happens to land first.
  const orderRef = useRef<string[]>([]);
  orderRef.current = displayChargepoints.map((cp) => cp.id);

  useEffect(() => {
    setExpanded({ id: null, byUser: false });
    setLiveOrder(null);
    orderLatchedRef.current = false;
    userToggledRef.current = false;
    allGunsRef.current.clear();
    liveInfoRef.current.clear();
  }, [open, location?.id]);

  const handleStatusResolved = useCallback((chargePointId: string, info: ChargePointStatusInfo) => {
    allGunsRef.current.set(chargePointId, info.connectorIds);
    liveInfoRef.current.set(chargePointId, info);

    // Rank once, when every charger has answered. A partial ranking would be
    // rewritten a moment later, and re-ranking on each 5s poll would move a
    // charger out from under the finger reaching for it.
    const expectedIds = orderRef.current;
    if (
      !orderLatchedRef.current &&
      expectedIds.length > 0 &&
      expectedIds.every((id) => liveInfoRef.current.get(id)?.loaded)
    ) {
      // A charger holding a plugged-in gun leads: someone standing at the station
      // with a gun already docked is here to start that session, so it outranks a
      // charger that merely has a free gun.
      const rank = (id: string): number => {
        const cp = liveInfoRef.current.get(id);
        if (!cp || cp.isOnline === false) return 3;
        if (cp.hasPreparing) return 0;
        return cp.hasAvailable ? 1 : 2;
      };
      // Stable sort, so chargers of equal rank keep the payload's order.
      orderLatchedRef.current = true;
      setLiveOrder([...expectedIds].sort((a, b) => rank(a) - rank(b)));
    }

    if (userToggledRef.current) return;

    // Nothing opens on arrival when there is a choice to make — an expanded card
    // pushes the rest of the guns off the screen the list exists to show. The one
    // exception is a lone gun: there is nothing to compare it against, so opening
    // it hides nothing and saves a tap. A gun named by QR or a deep link is
    // handled by ChargePointDetailSheet's scannedGun, not here.
    let pick: string | null = null;
    const every = orderRef.current.flatMap((cpId) => allGunsRef.current.get(cpId) ?? []);
    if (every.length === 1) pick = every[0];

    setExpanded((prev) => (prev.id === pick ? prev : { id: pick, byUser: false }));
  }, []);

  // Children compare this by reference, so it must not be rebuilt each render.
  const statusHandlers = useMemo(() => {
    const handlers = new Map<string, (info: ChargePointStatusInfo) => void>();
    for (const cpId of chargepoints.map((cp) => cp.id)) {
      handlers.set(cpId, (info) => handleStatusResolved(cpId, info));
    }
    return handlers;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [chargepoints.map((cp) => cp.id).sort().join(','), handleStatusResolved]);

  const handleToggleConnector = useCallback((connectorId: string) => {
    userToggledRef.current = true;
    setExpanded((prev) => ({ id: prev.id === connectorId ? null : connectorId, byUser: true }));
  }, []);

  const realtimeStatus = useLocationRealtimeStatus(location?.id ?? null);

  const { availableCount, totalCount } = useMemo(() => {
    // Priority 1: Real-time data from polling (already available from map subscription)
    if (realtimeStatus) {
      return {
        availableCount: realtimeStatus.availableEvses,
        totalCount: realtimeStatus.totalEvses,
      };
    }
    // Priority 2: Compute from chargepoints (fresh or static)
    let available = 0;
    const total = chargepoints.length;
    for (const cp of chargepoints) {
      if (cp.isOnline && cp.status === 'available') {
        available++;
      }
    }
    return { availableCount: available, totalCount: total };
  }, [realtimeStatus, chargepoints]);

  if (!location) return null;

  // Carousel settings
  const carouselSettings = {
    dots: true,
    infinite: location.photos && location.photos.length > 1,
    speed: 500,
    slidesToShow: 1,
    slidesToScroll: 1,
    arrows: true,
    adaptiveHeight: false,
  };

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="bottom" className="h-[88vh] rounded-t-[32px] border-0 p-0 bg-white dark:bg-gray-900" showCloseButton={false}>
        {/* Visually hidden title for accessibility */}
        <SheetTitle className="sr-only">{location.name}</SheetTitle>
        <SheetDescription className="sr-only">
          Charging station details - {formatDistance(location.distance)} km away with {location.chargepoints.length} chargepoints
        </SheetDescription>
        
        {/* Handle Bar */}
        <div className="relative flex justify-center pt-3 pb-2">
          <div className="w-12 h-1 bg-gray-300 dark:bg-gray-700 rounded-full"></div>
          <div className="absolute right-4 top-1/2 -translate-y-1/2">
            <button
              onClick={() => onOpenChange(false)}
              className="w-8 h-8 rounded-full bg-gray-100 dark:bg-gray-800 hover:bg-gray-200 dark:hover:bg-gray-700 flex items-center justify-center transition-colors"
            >
              <X className="w-4 h-4 text-gray-500 dark:text-gray-400" />
            </button>
          </div>
        </div>

        {/* Cover Photo with Overlay */}
        {location.coverPhoto && (
          <div className="relative h-48 overflow-hidden">
            <img
              src={location.coverPhoto}
              alt={location.name}
              className="w-full h-full object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/20 to-transparent" />
            <button
              onClick={() => location && toggleFavorite(location.id)}
              className="absolute top-4 left-4 w-10 h-10 rounded-full bg-white/90 dark:bg-gray-800/90 backdrop-blur-sm hover:bg-white dark:hover:bg-gray-800 flex items-center justify-center transition-colors shadow-lg z-10"
              aria-label={isFav ? 'Remove from favorites' : 'Add to favorites'}
            >
              <Heart className={`w-5 h-5 ${isFav ? 'text-red-500 fill-red-500' : 'text-gray-600 dark:text-gray-300'}`} />
            </button>
            <button
              onClick={() => onOpenChange(false)}
              className="absolute top-4 right-4 w-10 h-10 rounded-full bg-white/90 dark:bg-gray-800/90 backdrop-blur-sm hover:bg-white dark:hover:bg-gray-800 flex items-center justify-center transition-colors shadow-lg z-10"
            >
              <X className="w-5 h-5 text-gray-900 dark:text-white" />
            </button>
            <div className="absolute bottom-4 left-5 right-5 text-white">
              <h2 className="font-bold text-xl mb-1">{location.name}</h2>
              <div className="flex items-center gap-3">
                <button className="flex items-center gap-1.5 text-white/90 hover:text-white transition-colors group">
                  <Navigation className="w-4 h-4" />
                  <span className="text-sm font-medium">{formatDistance(location.distance)} km away</span>
                </button>
                {/* Real-time availability count */}
                {totalCount > 0 && (
                  <span className="flex items-center gap-1.5 text-white/90">
                    <PlugZap className="w-4 h-4" />
                    <span className="text-sm font-medium">{availableCount}/{totalCount} available</span>
                  </span>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Favorite button when no cover photo */}
        {!location.coverPhoto && (
          <div className="px-5 pt-2 pb-0 flex items-center justify-between">
            <h2 className="font-bold text-lg text-gray-900 dark:text-white">{location.name}</h2>
            <button
              onClick={() => location && toggleFavorite(location.id)}
              className="w-9 h-9 rounded-full bg-gray-100 dark:bg-gray-800 hover:bg-gray-200 dark:hover:bg-gray-700 flex items-center justify-center transition-colors"
              aria-label={isFav ? 'Remove from favorites' : 'Add to favorites'}
            >
              <Heart className={`w-4.5 h-4.5 ${isFav ? 'text-red-500 fill-red-500' : 'text-gray-500 dark:text-gray-400'}`} />
            </button>
          </div>
        )}

        {/* Content */}
        <div className="overflow-y-auto px-5 pt-4 pb-[max(1rem,env(safe-area-inset-bottom))]" style={{ maxHeight: location.coverPhoto ? 'calc(88vh - 240px)' : 'calc(88vh - 100px)' }}>

          {/* Address */}
          {location.address && (
            <div className="mb-5 flex items-start gap-2">
              <MapPin className="w-4 h-4 text-[#8bc43e] mt-0.5 flex-shrink-0" />
              <div className="flex-1">
                <p className="text-sm text-gray-600 dark:text-gray-300 leading-relaxed">{location.address}</p>
              </div>
              {/* Navigate Icon Button */}
              <button
                onClick={() => {
                  const [lat, lng] = location.position;
                  const url = `https://www.google.com/maps/dir/?api=1&destination=${lat},${lng}`;
                  window.open(url, '_blank');
                }}
                className="flex-shrink-0 w-9 h-9 bg-gradient-to-r from-[#8bc43e] to-[#6fa02e] hover:shadow-lg text-white rounded-xl transition-all duration-200 active:scale-95 flex items-center justify-center"
                title="Navigate with Google Maps"
              >
                <CarFront className="w-5 h-5" />
              </button>
            </div>
          )}

          {/* Guns, flattened up from the charge point level.
              87% of locations have a single charge point, so an intermediate
              charge point list was a one-item list and a wasted tap. The charge
              point name only appears as a group header when there is more than
              one to tell apart. */}
          <div className="mb-5">
            <div className="space-y-4">
              {displayChargepoints.map((chargepoint) => (
                <ChargePointConnectors
                  key={chargepoint.id}
                  chargePoint={toDetailedChargePoint(chargepoint)}
                  enabled={open}
                  showHeader
                  expandedConnectorId={expandedConnectorId}
                  expandedByUser={expanded.byUser}
                  onToggleConnector={handleToggleConnector}
                  onStatusResolved={statusHandlers.get(chargepoint.id)}
                  onStartCharging={onStartCharging}
                  isStartingCharge={isStartingCharge}
                />
              ))}
            </div>
          </div>

          {/* Facilities */}
          {location.facilities && location.facilities.length > 0 && (
            <div className="mb-5">
              <h3 className="font-semibold text-gray-900 dark:text-white mb-3">Facilities</h3>
              <div className="grid grid-cols-4 gap-3">
                {location.facilities.map((facility, index) => {
                  const { icon: Icon, label } = getFacilityIcon(facility);
                  return (
                    <div key={index} className="flex flex-col items-center gap-2 p-3 bg-gray-50 dark:bg-gray-800 rounded-xl hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors">
                      <div className="w-10 h-10 bg-white dark:bg-gray-700 rounded-lg flex items-center justify-center shadow-sm">
                        <Icon className="w-5 h-5 text-[#8bc43e]" />
                      </div>
                      <span className="text-xs text-gray-700 dark:text-gray-300 text-center font-medium">{label}</span>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Photos Carousel */}
          {location.photos && location.photos.length > 0 && (
            <div className="mb-4">
              <h3 className="font-semibold text-gray-900 dark:text-white mb-3">Photos</h3>
              <div className="rounded-2xl overflow-hidden bg-gray-100 dark:bg-gray-800">
                <Slider {...carouselSettings}>
                  {location.photos.map((photo, index) => (
                    <div key={index} className="h-56">
                      <img 
                        src={photo} 
                        alt={`${location.name} - Photo ${index + 1}`}
                        className="w-full h-full object-cover"
                      />
                    </div>
                  ))}
                </Slider>
              </div>
            </div>
          )}
        </div>
      </SheetContent>
    </Sheet>
  );
}